import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Select, InputNumber, Switch, Space, message, Popconfirm, Tag, Row, Col, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined, FilePdfOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Option } = Select;

function ManometroForm({ laudoId, tagVaso, origem = 'laudo' }) {
  const [manometros, setManometros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [padroes, setPadroes] = useState([]);
  // Campos globais compartilhados por todos os manômetros
  const [validadeGlobal, setValidadeGlobal] = useState(1);
  const [padraoGlobal, setPadraoGlobal] = useState({});
  const [padraoIdGlobal, setPadraoIdGlobal] = useState(null);

  useEffect(() => { if (laudoId) carregar(); carregarPadroes(); }, [laudoId]);

  const carregar = async () => {
    try {
      const r = await api.get(`/instrumentos/${laudoId}/manometros?origem=${origem}`);
      const data = r.data.map(m => ({ ...m, _form: m }));
      setManometros(data);
      // Carregar validade e padrao do primeiro manometro
      if (data.length > 0) {
        setValidadeGlobal(data[0].validade_anos || 1);
        // A selecao do padrao tambem precisa voltar: sem isto o campo
        // abria vazio mesmo com o padrao ja gravado no laudo.
        setPadraoIdGlobal(data[0].padrao_id || null);
        setPadraoGlobal({
          padrao_identificacao: data[0].padrao_identificacao,
          padrao_marca: data[0].padrao_marca,
          padrao_certificado: data[0].padrao_certificado,
          padrao_calibrado_por: data[0].padrao_calibrado_por,
          padrao_validade: data[0].padrao_validade,
        });
      }
    } catch {}
  };

  const carregarPadroes = async () => {
    try {
      const r = await api.get('/padroes-calibracao');
      setPadroes(r.data.filter(p => p.tipo === 'manometro' || p.tipo === 'manovacuometro'));
    } catch {}
  };

  const adicionar = async () => {
    if (!laudoId) { message.warning('Salve o laudo primeiro!'); return; }
    try {
      const r = await api.post(`/instrumentos/${laudoId}/manometros?origem=${origem}`, { 
        tag: '', calibrado: false, escala_unidade: 'kgf', validade_anos: validadeGlobal, fator_k: 2, local_instalacao: (tagVaso || '').trim() || null,
        ...padraoGlobal, padrao_id: padraoIdGlobal
      });
      setManometros([...manometros, { ...r.data, _form: r.data }]);
    } catch { message.error('Erro ao adicionar manômetro'); }
  };

  const salvar = async (ma) => {
    try {
      setLoading(true);
      await api.put(`/instrumentos/${laudoId}/manometros/${ma.id}?origem=${origem}`, { ...ma, validade_anos: validadeGlobal, ...padraoGlobal, padrao_id: padraoIdGlobal });
      message.success('Manômetro salvo!');
      carregar();
    } catch { message.error('Erro ao salvar'); }
    finally { setLoading(false); }
  };

  const excluir = async (id) => {
    try {
      await api.delete(`/instrumentos/${laudoId}/manometros/${id}?origem=${origem}`);
      setManometros(manometros.filter(m => m.id !== id));
      message.success('Manômetro removido!');
    } catch { message.error('Erro ao excluir'); }
  };

  const gerarPDF = async (ma) => {
    try {
      await api.get(`/laudos/${laudoId}/manometros/${ma.id}/pdf?origem=${origem}`, { responseType: 'blob' });
      message.success('Certificado de calibração gerado e salvo com sucesso!');
    } catch { message.error('Erro ao gerar PDF'); }
  };

  const atualizar = (id, campo, valor) => {
    setManometros(manometros.map(m => m.id === id ? { ...m, [campo]: valor } : m));
  };

  const selecionarPadraoGlobal = (padraoId) => {
    const padrao = padroes.find(p => p.id === padraoId);
    if (padrao) {
      setPadraoIdGlobal(padraoId);
      setPadraoGlobal({
        padrao_identificacao: padrao.identificacao,
        padrao_marca: padrao.marca,
        padrao_certificado: padrao.certificado,
        padrao_calibrado_por: padrao.calibrado_por,
        padrao_validade: padrao.validade,
      });
    }
  };

  if (!laudoId) return <div style={{ padding: 12, background: '#fffbe6', borderRadius: 8 }}>Salve o laudo primeiro para adicionar manômetros.</div>;

  return (
    <div>
      {/* Campos globais */}
      <div style={{ background: '#f0f7ff', border: '1px solid #91d5ff', borderRadius: 8, padding: 12, marginBottom: 16 }}>
        <div style={{ fontWeight: 'bold', marginBottom: 10 }}>Configurações gerais — Manômetros</div>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <label style={{ fontWeight: 'bold' }}>Validade (todos):</label>
            <Select value={validadeGlobal} onChange={v => setValidadeGlobal(v)} style={{ width: '100%' }}>
              <Option value={1}>1 ano</Option>
              <Option value={2}>2 anos</Option>
              <Option value={3}>3 anos</Option>
              <Option value={4}>4 anos</Option>
              <Option value={5}>5 anos</Option>
            </Select>
          </Col>
        </Row>
        <div style={{ fontWeight: 'bold', marginTop: 10, marginBottom: 6 }}>Padrão Utilizado (todos):</div>
        {padroes.length > 0 && (
          <Row gutter={8} style={{ marginBottom: 8 }}>
            <Col span={24}>
              <Select placeholder="Selecione o padrão cadastrado" style={{ width: '100%' }} onChange={selecionarPadraoGlobal} value={padraoIdGlobal} allowClear>
                {padroes.map(p => <Option key={p.id} value={p.id}>{p.tipo === 'manometro' ? 'Manômetro' : 'Manovacuômetro'} — {p.identificacao} {p.marca ? '(' + p.marca + ')' : ''}</Option>)}
              </Select>
            </Col>
          </Row>
        )}
        <Row gutter={8}>
          <Col span={6}><label>Identificação</label><Input size="small" value={padraoGlobal.padrao_identificacao||''} onChange={e => setPadraoGlobal(prev => ({...prev, padrao_identificacao: e.target.value}))} /></Col>
          <Col span={6}><label>Marca</label><Input size="small" value={padraoGlobal.padrao_marca||''} onChange={e => setPadraoGlobal(prev => ({...prev, padrao_marca: e.target.value}))} /></Col>
          <Col span={6}><label>Certificado Nº</label><Input size="small" value={padraoGlobal.padrao_certificado||''} onChange={e => setPadraoGlobal(prev => ({...prev, padrao_certificado: e.target.value}))} /></Col>
          <Col span={6}><label>Calibrado por</label><Input size="small" value={padraoGlobal.padrao_calibrado_por||''} onChange={e => setPadraoGlobal(prev => ({...prev, padrao_calibrado_por: e.target.value}))} /></Col>
        </Row>
      </div>

      <Button icon={<PlusOutlined />} type="dashed" onClick={adicionar} style={{ marginBottom: 16 }}>Adicionar Manômetro</Button>

      {manometros.map((ma, idx) => (
        <Card key={ma.id} size="small" style={{ marginBottom: 12 }}
          title={<span>Manômetro {idx + 1} {ma.tag ? <Tag color="blue">{ma.tag}</Tag> : ''}</span>}
        >
          <Row gutter={16}>
            <Col span={4}><label>TAG</label><Input value={ma.tag||''} onChange={e => atualizar(ma.id,'tag',e.target.value)} placeholder="MA-01" /></Col>
            <Col span={4}><label>Marca</label><Input value={ma.marca||''} onChange={e => atualizar(ma.id,'marca',e.target.value)} /></Col>
            <Col span={4}><label>Tipo</label>
              <Select value={ma.tipo||''} onChange={v => atualizar(ma.id,'tipo',v)} style={{ width: '100%' }}>
                <Option value="analogico">Analógico</Option>
                <Option value="digital">Digital</Option>
              </Select>
            </Col>
            <Col span={4}><label>Classe</label><Input value={ma.classe||''} onChange={e => atualizar(ma.id,'classe',e.target.value)} /></Col>
            <Col span={4}><label>Tamanho Visor</label><Input value={ma.tamanho_visor||''} onChange={e => atualizar(ma.id,'tamanho_visor',e.target.value)} placeholder='4"' /></Col>
            <Col span={4}><label>Local Instalação</label><Input value={ma.local_instalacao||''} onChange={e => atualizar(ma.id,'local_instalacao',e.target.value)} placeholder="VP-01" /></Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={4}><label>Escala Mín.</label><InputNumber value={ma.escala_min} onChange={v => atualizar(ma.id,'escala_min',v)} style={{ width: '100%' }} decimalSeparator="," precision={2} /></Col>
            <Col span={4}><label>Escala Máx.</label><InputNumber value={ma.escala_max} onChange={v => atualizar(ma.id,'escala_max',v)} style={{ width: '100%' }} decimalSeparator="," precision={2} /></Col>
            <Col span={4}><label>Unidade</label>
              <Select value={ma.escala_unidade||'kgf'} onChange={v => atualizar(ma.id,'escala_unidade',v)} style={{ width: '100%' }}>
                <Option value="kgf">kgf/cm²</Option><Option value="bar">bar</Option><Option value="psi">psi</Option><Option value="mpa">MPa</Option>
              </Select>
            </Col>
            <Col span={4}><label>Resolução</label><InputNumber value={ma.resolucao} onChange={v => atualizar(ma.id,'resolucao',v)} style={{ width: '100%' }} decimalSeparator="," precision={2} /></Col>
            <Col span={4}><label>Conexão</label>
              <Select value={ma.conexao||''} onChange={v => atualizar(ma.id,'conexao',v)} style={{ width: '100%' }}>
                <Option value="1/4">1/4"</Option><Option value="1/2">1/2"</Option><Option value="3/4">3/4"</Option><Option value="outra">Outra</Option>
              </Select>
            </Col>
            <Col span={4}><label>Resultado</label>
              <Select value={ma.resultado_laudo||''} onChange={v => atualizar(ma.id,'resultado_laudo',v)} style={{ width: '100%' }}
                placeholder="Automático">
                <Option value="">Automático</Option>
                <Option value="APROVADO">Aprovado</Option>
                <Option value="REPROVADO">Reprovado</Option>
              </Select>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={24}>
              <label style={{ fontWeight: 'bold' }}>Observações:</label>
              <Input.TextArea value={ma.observacoes||''} onChange={e => atualizar(ma.id,'observacoes',e.target.value)} rows={2} style={{ marginTop: 4 }} />
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={4}><label>Calibrado?</label><br/><Switch checked={ma.calibrado} onChange={v => atualizar(ma.id,'calibrado',v)} checkedChildren="Sim" unCheckedChildren="Não" /></Col>
            <Col span={4}><label>Danificado?</label><br/><Switch checked={ma.danificado} onChange={v => atualizar(ma.id,'danificado',v)} checkedChildren="Sim" unCheckedChildren="Não" /></Col>
            <Col span={4}><label>Substituído?</label><br/><Switch checked={ma.substituido} onChange={v => atualizar(ma.id,'substituido',v)} checkedChildren="Sim" unCheckedChildren="Não" /></Col>
          </Row>
          {ma.calibrado && (
            <div style={{ marginTop: 12, padding: 12, background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
              <Row gutter={16} style={{ marginBottom: 8 }}>
                <Col span={6}><label style={{ fontWeight: 'bold' }}>Unidade das leituras:</label>
                  <Select value={ma.unidade_calibracao||'kgf'} onChange={v => atualizar(ma.id,'unidade_calibracao',v)} style={{ width: '100%' }}>
                    <Option value="kgf">kgf/cm²</Option><Option value="bar">bar</Option><Option value="psi">psi</Option><Option value="mpa">MPa</Option>
                  </Select>
                </Col>
                <Col span={4}>
                  <label style={{ fontWeight: 'bold' }}>Fator K:</label>
                  <InputNumber value={ma.fator_k ?? 2} onChange={v => atualizar(ma.id,'fator_k',v)} style={{ width: '100%' }} precision={0} min={1} max={3} />
                </Col>
                <Col span={6}>
                  <label style={{ fontWeight: 'bold' }}>Incerteza expandida (U = k × u):</label>
                  {(() => {
                    const erros = [];
                    for (let i = 1; i <= 5; i++) {
                      const vm = ma[`leitura_ma_${i}`];
                      const carga = ma[`vvc_carga_${i}`];
                      const desc = ma[`vvc_desc_${i}`];
                      if (vm != null && carga != null) erros.push(carga - vm);
                      if (vm != null && desc != null) erros.push(desc - vm);
                    }
                    if (erros.length < 2) return <div style={{ color: '#aaa', fontSize: 12 }}>Preencha as leituras</div>;
                    const media = erros.reduce((a, b) => a + b, 0) / erros.length;
                    const variancia = erros.reduce((a, b) => a + Math.pow(b - media, 2), 0) / (erros.length - 1);
                    const u = Math.sqrt(variancia);
                    const k = ma.fator_k ?? 2;
                    const U = k * u;
                    return <div style={{ fontWeight: 'bold', fontSize: 14, color: '#1a6ca8', padding: '4px 0' }}>U = {U.toFixed(4).replace('.', ',')} {ma.unidade_calibracao || 'kgf'}</div>;
                  })()}
                </Col>
              </Row>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 6 }}>Leituras de Calibração — Valor de Medição (VM)</label>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#1a6ca8', color: 'white' }}>
                    <th style={{ padding: '4px 8px', border: '1px solid #ccc' }}>VM ({ma.unidade_calibracao||'kgf'})</th>
                    <th style={{ padding: '4px 8px', border: '1px solid #ccc' }}>VVC Carga</th>
                    <th style={{ padding: '4px 8px', border: '1px solid #ccc' }}>VVC Descarga</th>
                  </tr>
                </thead>
                <tbody>
                  {[1,2,3,4,5].map(i => (
                    <tr key={i}>
                      <td style={{ padding: 4, border: '1px solid #ddd' }}><InputNumber value={ma[`leitura_ma_${i}`]} onChange={v => atualizar(ma.id,`leitura_ma_${i}`,v)} style={{ width: '100%' }} precision={3} decimalSeparator="," /></td>
                      <td style={{ padding: 4, border: '1px solid #ddd' }}><InputNumber value={ma[`vvc_carga_${i}`]} onChange={v => atualizar(ma.id,`vvc_carga_${i}`,v)} style={{ width: '100%' }} precision={3} decimalSeparator="," /></td>
                      <td style={{ padding: 4, border: '1px solid #ddd' }}><InputNumber value={ma[`vvc_desc_${i}`]} onChange={v => atualizar(ma.id,`vvc_desc_${i}`,v)} style={{ width: '100%' }} precision={3} decimalSeparator="," /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Divider />
          <Space>
            <Button size="small" type="primary" icon={<FilePdfOutlined />} style={{ color: '#cf1322', borderColor: '#cf1322', background: 'white' }} onClick={() => gerarPDF(ma)}>Gerar Laudo Calibração</Button>
            <Button size="small" type="primary" onClick={() => salvar(ma)} loading={loading}>Salvar</Button>
            <Popconfirm title="Excluir manômetro?" onConfirm={() => excluir(ma.id)} okText="Sim" cancelText="Não">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        </Card>
      ))}
    </div>
  );
}

export default ManometroForm;
